import { AxiosError } from 'axios';
import { TokenExpiredError } from '@honeybook/core';
import { HB_BASE_ERROR_CODES } from '@honeybook/utils';
const getErrorCode = (error) => error?.code;
/** The connection carried nothing back — offline, DNS failure, dropped socket, blocked by CORS. */
export function isAxiosNetworkError(error) {
    return getErrorCode(error) === AxiosError.ERR_NETWORK;
}
/** We gave up waiting before the server answered. */
export function isAxiosTimeoutError(error) {
    const code = getErrorCode(error);
    // Axios reports a timeout as ECONNABORTED, or as ETIMEDOUT when
    // `transitional.clarifyTimeoutError` is on. Same thing to a caller.
    return code === AxiosError.ECONNABORTED || code === AxiosError.ETIMEDOUT;
}
/**
 * True when the request failed before any response from the server reached us.
 * Those are the failures worth resending: the server may still have applied the
 * request, and reconciling that is the caller's job.
 *
 * Classified on the axios error code, deliberately. Two readings that look
 * equivalent are not:
 *
 * - "the error has no `response`" — on a transport failure axios puts the
 *   XMLHttpRequest itself on `response`, so a real outage looks like it has one,
 *   while an exception thrown by our own code over a *successful* response
 *   genuinely has none. Inverted in both directions.
 * - "the message mentions the network" — `networkLabel` is an identifier in the
 *   request pipeline, so our own crashes read as an outage.
 *
 * Both mistakes end the same way: a client-side exception resent forever,
 * blocking every later request behind it.
 *
 * An error this cannot place — an empty rejection, a cancellation, anything the
 * server answered — is not a network error. Saying "no" costs a retry; saying
 * "yes" wrongly costs the user's work.
 */
export function isNetworkError(error) {
    return isAxiosNetworkError(error) || isAxiosTimeoutError(error);
}
/**
 * Check if error is a CORS error
 * CORS errors typically have no response and specific error patterns
 */
export function isCorsError(error) {
    if (!error || typeof error !== 'object') {
        return false;
    }
    const err = error;
    const response = err.response;
    const hasResponse = !!response;
    return ((typeof err.message === 'string' &&
        err.message.toLowerCase().includes('cors')) ||
        err.code === 'ERR_CORS' ||
        (response?.status === 0 && hasResponse) ||
        (err.code === 'ERR_NETWORK' && hasResponse && !response?.status));
}
/**
 * The session is gone: the server answered with the HB unauthorized error, or
 * `BaseHttpClient` already turned that answer into a `TokenExpiredError`.
 */
export function isUnauthorizedError(error) {
    if (error instanceof TokenExpiredError) {
        return true;
    }
    const errorType = error?.response?.data?.error_type;
    return errorType === HB_BASE_ERROR_CODES.unauthorized_error;
}
//# sourceMappingURL=request-error.utils.js.map